/**
 * @file        job/download-slices.js
 * @author      Ryan Rossiter, ryan@kingsds.network
 *              Robert Mirandola, <robert@kingsds.network>
 *              Will Pringle, <will@distributive.network>
 * @date        June 2020
 *              July 2022
 *              November 2023
 *
 * Utility functions for fetching and decoding results for a job.
 */
'use strict';

const { rehydrateRange } = require('../range-object');
const wallet     = require('dcp/dcp-client/wallet');
const protocolV4 = require('dcp/protocol');
const { fetchURI } = require('dcp/utils');
const { skipStorage } = require('dcp/common/scheduler-constants');
const debugging = require('dcp/debugging').scope('download-slices');

/**
 * Sends a request to the scheduler to fetch currently available results and decodes them.
 * If range is passed, it will only attempt to fetch results specified in that range.
 * @param {string} jobId
 * @param {RangeObject} [rangeObject] - range object to query results
 * @return {Array} resultsArray - array of objects with slice and value properties
 */
async function fetchResults(jobId, rangeObject)
{
  const range = rangeObject && rehydrateRange(rangeObject);
  const decodedResults = [];

  // Fetch any existing results
  const ownerKeystore = await wallet.getId();
  const conn = new protocolV4.Connection(dcpConfig.scheduler.services.resultSubmitter, { identity: ownerKeystore });

  /**
   * The ownerKeystore is used to authorize the request message to ensure that only the job owner 
   * can make a request to the 'fetchResults' operation.
   */
  const fetchResultMsg = new conn.Request({
    operation: 'fetchResult',
    data: {
      job: jobId,
      owner: ownerKeystore.address,
      range,
    }
  });
  await fetchResultMsg.authorize(ownerKeystore);

  const { success, payload } = await fetchResultMsg.send();

  if (!success)
  {
    debugging('fetch') && console.error('Error fetching results:', payload.message);
    console.warn('No results were found for the specified job id', jobId);
    return;
  }

  // Unpack results, using fetchURI to decode/fetch result URIs
  await Promise.all(payload.map(async r => {
    var value;
    if (!r) {
      console.warn(`fetchResults: Received result was not defined (${r}), ignoring...`);
      return;
    }

    const decodedValue = decodeURIComponent(r.value);
    if (decodedValue === skipStorage)
      value = decodedValue;
    else
    {
      try
      {
        /**
         * There is currently not way to access OAM instance on the job level.
         * This case is similar to the handleResult case in job/index.js.
         * The value to fetchURI is either a dataURI, RDS that user defined or scheduler disk location.
         */
        value = await fetchURI(decodedValue, false);
      }
      catch (error)
      {
        throw new Error(`Error in fetchResults(): ${error.message}`);
      }
    }

    decodedResults.push({
      slice: r.slice,
      value,
    });
  }));
  conn.close();

  return decodedResults;
}

exports.fetchResults = fetchResults;
